/**
 * WorkerPool child spawn and readiness lifecycle.
 *
 * @module worker-pool/spawn-lifecycle
 */

import type { BrowserLogger } from '../logger.js';
import { wrapChild } from './child-handle.js';
import type { PooledChild } from './bookkeeping.js';
import { WorkerPoolError } from './errors.js';
import type { WorkerDriver } from './types.js';

type WorkerPoolSpawn = typeof import('node:child_process').spawn;

export interface SpawnWorkerChildOptions<Handle> {
  readonly driver: WorkerDriver<Handle>;
  readonly spawn: WorkerPoolSpawn;
  readonly children: Set<PooledChild<Handle>>;
  readonly replacement: boolean;
  readonly readyTimeoutMs: number;
  readonly now: () => number;
  readonly logger: BrowserLogger;
  readonly isDraining: () => boolean;
  readonly queuedWaiterCount: () => number;
  readonly onPoolBecameActive: () => void;
  readonly onSpawnFailedWithQueuedWaiters: () => void;
  readonly retire: (worker: PooledChild<Handle>, error: Error, force?: boolean) => void;
  readonly killWorker: (worker: PooledChild<Handle>) => void;
}

/**
 * Spawn a child and drive the readiness handshake.
 *
 * `replacement` marks spawns kicked off to refill a lost slot. Their failures
 * are handled by the caller's single replacement catch, so they must not recurse
 * into another replacement attempt here.
 */
export const spawnWorkerChild = <Handle>({
  driver,
  spawn,
  children,
  replacement,
  readyTimeoutMs,
  now,
  logger,
  isDraining,
  queuedWaiterCount,
  onPoolBecameActive,
  onSpawnFailedWithQueuedWaiters,
  retire,
  killWorker,
}: SpawnWorkerChildOptions<Handle>): Promise<PooledChild<Handle>> => {
  const { execPath, modulePath, argv = [] } = driver.entry;
  const child = spawn(execPath, [modulePath, ...argv], {
    stdio: ['pipe', 'pipe', 'inherit'],
    // A `killProcessGroup` driver's child leads its own process group so the
    // pool can SIGKILL the whole group (reaping a runner's spawned browser).
    ...(driver.killProcessGroup ? { detached: true } : {}),
  });
  // Count the child the instant it is spawned — including the spawn/handshake
  // window — so `size` reflects in-flight spawns. This is what makes a respawn
  // restore capacity immediately and a queued caller park (rather than racing a
  // second spawn) while the first child is still readying.
  const worker: PooledChild<Handle> = {
    child: wrapChild(child),
    handle: undefined,
    current: null,
    lease: null,
    retireOnRelease: null,
    retired: false,
    readiedAt: now(), // fallback; overwritten when the readiness handshake resolves
  };
  children.add(worker);
  onPoolBecameActive();

  return new Promise<PooledChild<Handle>>((resolve, reject) => {
    let ready = false;
    // Local idempotency flag (NOT worker.retired — drain() sets that, and
    // reusing it would suppress the reject when a mid-spawn child is killed
    // during drain). The SIGKILL below fires its own `exit`, which re-enters the
    // exit handler; `spawnFailed` makes that a no-op.
    let spawnFailed = false;
    const failSpawn = (err: WorkerPoolError): void => {
      if (ready || spawnFailed) return;
      spawnFailed = true;
      clearTimeout(readyTimer);
      children.delete(worker);
      killWorker(worker);
      reject(err);
      // The slot we counted on never materialized. The caller awaiting this
      // spawn was just rejected, but anyone parked in acquire() while it was
      // in flight would hang — `acquire` only ever re-drives waiters via a
      // released/replacement child. Refill the lost slot once for them.
      // Skipped for `replacement` spawns (their failure is already handled by
      // spawnReplacement's catch) so this stays a single bounded attempt.
      const queued = queuedWaiterCount();
      if (!replacement && !isDraining() && queued > 0) {
        logger.warn('[WorkerPool] spawn failed with queued waiters; replacing once', {
          error: err.message,
          queued,
        });
        onSpawnFailedWithQueuedWaiters();
      }
    };
    const readyTimer = setTimeout(
      () => failSpawn(new WorkerPoolError('worker did not become ready in time')),
      readyTimeoutMs
    );

    // A post-ready driver fatal means the wire/protocol is broken — the handle
    // is unusable, so a 'drain' policy can't preserve the session. Force-retire.
    driver.ready(worker.child, { onFatal: (err) => retire(worker, err, true) }).then(
      (handle) => {
        if (spawnFailed) return; // lost a race with an early exit/error
        ready = true;
        clearTimeout(readyTimer);
        worker.handle = handle;
        worker.readiedAt = now(); // anchor the breaker's lifetime window to readiness
        resolve(worker);
      },
      (err: unknown) =>
        failSpawn(
          new WorkerPoolError(
            `worker not ready: ${err instanceof Error ? err.message : String(err)}`
          )
        )
    );

    child.on('exit', (code, signal) => {
      if (!ready) {
        failSpawn(
          new WorkerPoolError(`worker exited before ready (code=${code}, signal=${signal})`)
        );
        return;
      }
      if (!worker.retired) {
        // The child is already gone — force past any 'drain' deferral.
        retire(
          worker,
          new WorkerPoolError(`worker exited unexpectedly (code=${code}, signal=${signal})`),
          true
        );
      }
    });
    child.on('error', (err) => {
      if (!ready) {
        failSpawn(new WorkerPoolError(`worker spawn failed: ${err.message}`));
      } else if (!worker.retired) {
        // A post-ready EventEmitter 'error' would otherwise throw uncaught; the
        // child is broken, so force past any 'drain' deferral.
        retire(worker, new WorkerPoolError(`worker error: ${err.message}`), true);
      }
    });
  });
};
